// src/hooks/useCurrentPage.js
//
// Spec 20260925-ui-feedback §6.2, plan T1.23 / §13.1 / §13.4. Tasks T043.
//
// Which page is the user on — as a PATTERN, never a path.
//
//   route     — the <Route path> pattern (`/alarms/:id`), via React Router's matchRoutes
//               over src/generated/routePatterns.json (App.js has JSX <Routes>, no route
//               config object, so the pattern list is generated — see
//               scripts/generate-route-patterns.mjs). No match → null, and the widget
//               refuses to send rather than falling back to a raw path.
//   pageId    — findPageForPath(route) from @caro/shared: param-aware, longest match.
//   pageLabel — the nav label, from the SAME lookup Layout uses for document.title and the
//               Recent list (labelForNavPath — one source), else the registry label.
//   tab       — the tab a page reported (useReportTab), else `?tab=`.
//
// AC-6: an id in the URL (`/alarms/987654`) never survives into any of these fields.

import { useCallback, useMemo, useRef, useState } from 'react';
import { matchRoutes, useLocation } from 'react-router-dom';
import { findPageForPath } from '@caro/shared/constants';

import routePatterns from '../generated/routePatterns.json';

export const ROUTE_PATTERNS = Object.freeze([...(routePatterns.patterns || [])]);

const configFor = (patterns) => patterns.map((path) => ({ path }));
const DEFAULT_CONFIG = configFor(ROUTE_PATTERNS);

/**
 * The nav label for a path: the first nav item whose `path` the pathname starts with.
 * This is exactly the lookup Layout.js did twice (document.title and Recent pages);
 * both now call this. Returns null when nothing matches.
 *
 * @param {Array<{ text?: string, path?: string }>} navItems  flat, in nav order
 */
export function labelForNavPath(navItems, path) {
  if (!Array.isArray(navItems) || typeof path !== 'string') return null;
  const hit = navItems.find((i) => i && typeof i.path === 'string' && i.path && path.startsWith(i.path));
  return hit && hit.text ? hit.text : null;
}

/** Pure core of useCurrentPage — testable without a router. */
export function resolveCurrentPage({ pathname, search = '', navItems = [], reportedTab = null, patterns }) {
  const config = patterns ? configFor(patterns) : DEFAULT_CONFIG;
  let route = null;
  if (typeof pathname === 'string' && pathname) {
    const matches = matchRoutes(config, pathname);
    if (matches && matches.length) route = matches[matches.length - 1].route.path || null;
  }

  const page = route ? findPageForPath(route) : undefined;
  const pageId = page ? page.id : null;

  // Only a LABEL is looked up with the real pathname: nav labels are static strings, so no
  // id can come out of it. Without a route there is no page to label.
  const pageLabel = route ? labelForNavPath(navItems, pathname) || (page ? page.label : null) : null;

  let queryTab = null;
  try {
    queryTab = new URLSearchParams(search || '').get('tab');
  } catch {
    queryTab = null;
  }
  const tab = (typeof reportedTab === 'string' && reportedTab) || queryTab || null;

  return { route, pageId, pageLabel, tab };
}

/**
 * Hook used by Layout. Returns { page, reportTab } — exactly the CurrentPageContext value.
 * @param {{ navItems: Array<{ text?: string, path?: string }> }} args
 */
export function useCurrentPage({ navItems } = {}) {
  const location = useLocation();
  const [reported, setReported] = useState({ path: null, tab: null });
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // A report only counts for the path it was made on, so a stale tab never follows the
  // user onto the next page even for the one render before the page's cleanup runs.
  const reportTab = useCallback(
    (value) => setReported({ path: pathRef.current, tab: value || null }),
    [],
  );

  const reportedTab = reported.path === location.pathname ? reported.tab : null;

  const page = useMemo(
    () =>
      resolveCurrentPage({
        pathname: location.pathname,
        search: location.search,
        navItems,
        reportedTab,
      }),
    [location.pathname, location.search, navItems, reportedTab],
  );

  return useMemo(() => ({ page, reportTab }), [page, reportTab]);
}

export default useCurrentPage;
